import { useState } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface AccountFormProps {
  user: {
    name: string | null
    email_address: string
  }
  patchUrl: string
}

export default function AccountForm({ user, patchUrl }: AccountFormProps) {
  const profileForm = useForm({
    name: user.name || '',
    email_address: user.email_address,
  })

  const passwordForm = useForm({
    current_password: '',
    password: '',
    password_confirmation: '',
  })

  const [passwordMismatch, setPasswordMismatch] = useState(false)

  function handleProfileSubmit(e: React.FormEvent) {
    e.preventDefault()
    profileForm.patch(patchUrl, {
      // @ts-expect-error Inertia v2 data option
      data: { user: { name: profileForm.data.name, email_address: profileForm.data.email_address } },
    })
  }

  function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (passwordForm.data.password !== passwordForm.data.password_confirmation) {
      setPasswordMismatch(true)
      return
    }
    setPasswordMismatch(false)
    passwordForm.patch(patchUrl, {
      // @ts-expect-error Inertia v2 data option
      data: { section: 'password', current_password: passwordForm.data.current_password, password: passwordForm.data.password },
      onSuccess: () => {
        passwordForm.reset()
      },
    })
  }

  return (
    <>
      <header className="flex h-14 items-center border-b px-8">
        <h1 className="text-sm font-semibold">My Account</h1>
      </header>

      <div className="max-w-md space-y-10 overflow-y-auto p-8">
        {/* Profile */}
        <section>
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Profile
          </h2>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-xs">Name</Label>
              <Input
                id="name"
                value={profileForm.data.name}
                onChange={(e) => profileForm.setData('name', e.target.value)}
                className="mt-1 h-9 text-[13px]"
              />
            </div>
            <div>
              <Label htmlFor="email_address" className="text-xs">Email</Label>
              <Input
                id="email_address"
                type="email"
                required
                value={profileForm.data.email_address}
                onChange={(e) => profileForm.setData('email_address', e.target.value)}
                className="mt-1 h-9 text-[13px]"
              />
            </div>
            <Button type="submit" size="sm" disabled={profileForm.processing}>
              Save Profile
            </Button>
          </form>
        </section>

        {/* Password */}
        <section>
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Change Password
          </h2>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <Label htmlFor="current_password" className="text-xs">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                required
                value={passwordForm.data.current_password}
                onChange={(e) => passwordForm.setData('current_password', e.target.value)}
                className="mt-1 h-9 text-[13px]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs">New Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={passwordForm.data.password}
                onChange={(e) => passwordForm.setData('password', e.target.value)}
                className="mt-1 h-9 text-[13px]"
              />
            </div>
            <div>
              <Label htmlFor="password_confirmation" className="text-xs">Confirm New Password</Label>
              <Input
                id="password_confirmation"
                type="password"
                required
                value={passwordForm.data.password_confirmation}
                onChange={(e) => {
                  passwordForm.setData('password_confirmation', e.target.value)
                  setPasswordMismatch(false)
                }}
                className="mt-1 h-9 text-[13px]"
              />
              {passwordMismatch && (
                <p className="mt-1 text-xs text-red-600">Passwords do not match.</p>
              )}
            </div>
            <Button type="submit" size="sm" disabled={passwordForm.processing}>
              Change Password
            </Button>
          </form>
        </section>
      </div>
    </>
  )
}
